/**
 * The printed proposal in three looks, as designed in Figma
 * ("Abstract Creative · Proposal templates", rows E, S and R).
 *
 * Every page is a US Letter sheet, 816 × 1056 at 96dpi, laid out the way the
 * frames are: the chapter label at the top (a kicker in Editorial, the black
 * bar in Swiss grid, a red label under a giant outlined number in Bold red),
 * the content, a soft closing panel at 822px when the page has one, and the
 * footer (the solid mark, "Abstract Creative · Proposal for …" and the page
 * number) at 1006px.
 *
 * Up to seventeen kinds of page: cover, at a glance, letter, the challenge,
 * approach, scope, timeline, options, investment, case studies, profiles,
 * working together, the fine print and next steps. A page with nothing behind
 * it is left out and the contents list and page numbers close up around it.
 *
 * Nothing is shrunk to fit and nothing runs off a sheet: content longer than
 * a page (a long letter, many services, many clauses, many lines in the
 * price) continues on a page of its own, the way the Figma "Scope, 1 of 2 /
 * 2 of 2" does. Heights are estimated from the type sizes in the frames,
 * deliberately on the generous side, so a page breaks early rather than late.
 *
 * The same markup is the studio's editable canvas: with `editable`, the text
 * that maps to a section field carries data-edit (the studio makes it
 * contentEditable), and everything else is drawn read-only. A letter split
 * over two pages marks each part with data-part so the studio joins the parts
 * back into one field.
 */

import { LOOKS, DEFAULT_LOOK, STANDARD_COPY } from '../proposalLooks'
import { buildModel, money, text, plain, isHtml } from './model'
import { lockupSvg, markSvg } from './logo'
import { wrapCount } from './metrics'
import {
  STAY_IN_TOUCH, PRICE_COVERS, ON_THIS_PROJECT, OPTIONS_INCLUDE, GLANCE_NEXT,
  FIRST_TWO_WEEKS, WHERE_THINGS_STAND, HOW_DELIVERED, CHANGE_THE_DATES, SAME_TERMS, LETTER_PULL,
} from './copy'

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const pad = (n) => String(n).padStart(2, '0')

const fmtDate = (d) => {
  const [y, m, day] = String(d || '').slice(0, 10).split('-').map(Number)
  if (!y) return ''
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

const parasOf = (s) => text(s).split(/\n{2,}/).map(p => p.trim()).filter(Boolean)
const para = (p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`
const stripTags = (s) => String(s ?? '').replace(/<[^>]+>/g, '')

const weeksLabel = (a, b) => (a && b ? (a === b ? `Week ${a}` : `Weeks ${a}–${b}`) : '')

const NUMBER_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
  'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen', 'Twenty']
const inWords = (n) => NUMBER_WORDS[n] || String(n)

/* ── Measuring ─────────────────────────────────────────────────────────────
 * Lines a run of text takes at a size and width, wrapped word by word against
 * Public Sans' measured advance widths (metrics.js).
 */
export function linesOf(str, size, width, weight = 'r', tracking = 0) {
  return wrapCount(str, size, width, weight, tracking)
}
const tall = (str, size, width, lh, weight) => linesOf(str, size, width, weight) * size * lh

/** Each look's measures, read off the frames: side margin, text width, where the content starts, trio column. */
export const GEOMETRY = {
  editorial: { m: 72, w: 672, top: 128, cw: 200 },
  swiss: { m: 56, w: 704, top: 104, cw: 211 },
  bold: { m: 64, w: 688, top: 112, cw: 205 },
}
/** Where flowing content must stop: above the closing panel (822), or above the footer (1006). */
export const STOP_CLOSER = 800
export const STOP_FOOT = 968

/**
 * Greedy pagination: items ({ h, mb }) in order, onto pages that hold `first` px
 * on the first page and `next` px after. An item taller than a page goes on a
 * page of its own; it cannot be cut.
 */
export function paginate(items, first, next = first) {
  const pages = [[]]
  let used = 0
  let cap = first
  for (const it of items) {
    // An item's own space below it (mb) is not needed when it ends a page.
    if (pages[pages.length - 1].length && used + it.h - (it.mb || 0) > cap) {
      pages.push([])
      used = 0
      cap = next
    }
    pages[pages.length - 1].push(it)
    used += it.h
  }
  return pages
}

export function lookPages(doc = {}, { look, settings = {}, catalog = {}, editable = false } = {}) {
  const L = LOOKS[look] ? look : (LOOKS[doc.look] ? doc.look : DEFAULT_LOOK)
  const G = GEOMETRY[L]
  const m = buildModel(doc, { settings, catalog, resolve: !editable })
  const ed = (path) => (editable && path ? ` data-edit="${esc(path)}"` : '')
  const val = (v, path, placeholder) => {
    const t = text(v)
    if (t) return `<span${ed(path)}>${esc(t)}</span>`
    return editable && path ? `<span${ed(path)} class="token">${esc(placeholder)}</span>` : ''
  }
  const client = m.recipient || m.contact || 'you'
  const firstName = m.owner.name.split(' ')[0]
  const S = L === 'swiss'
  const B = L === 'bold'

  /* ── Page frame ──────────────────────────────────────────────────────── */

  const footer = (n) => `<div class="pp-foot">${markSvg({ size: 20 })}<span class="pp-foot-t">Abstract Creative&nbsp;&nbsp;·&nbsp;&nbsp;Proposal for ${esc(client)}</span><span class="pp-foot-n">${pad(n)}</span></div>`

  const header = (num, label) => {
    if (S) {
      return `<div class="pp-bar">${num ? `<span class="n">${pad(num)}</span>` : ''}<span class="l">${esc(label)}</span><span class="c">${esc(m.recipient)}</span></div>`
    }
    if (B) {
      return `<div class="pp-kicker">${esc(label)}</div>${num ? `<div class="pp-giant onum red" aria-hidden="true">${pad(num)}</div>` : ''}`
    }
    return `<div class="pp-kicker">${num ? `<span class="n">${pad(num)}</span>` : ''}<span>${esc(label)}</span></div>`
  }

  /** The page heading. Swiss sets the chapter number beside it, very large. */
  const heading = (num, h, sub = '', cls = '') => {
    const inner = `<h2 class="pp-h1${cls ? ` ${cls}` : ''}">${h}</h2>${sub ? `<p class="pp-sub">${sub}</p>` : ''}`
    if (S) return `<div class="pp-head">${num ? `<div class="pp-bignum onum" aria-hidden="true">${pad(num)}</div>` : '<div></div>'}<div class="pp-head-t">${inner}</div></div>`
    return `<div class="pp-head">${inner}</div>`
  }
  /** How much of the page a heading takes, with the space under it. */
  const headH = (h, sub = '', size = null) => {
    const t = stripTags(String(h).replace(/<br>/g, '\n'))
    if (S) {
      const own = linesOf(t, 30, 454, 'k', -0.01) * 32.4 + (sub ? 12 + tall(sub, 13, 454, 1.55) : 0)
      return Math.max(108, own - 12) + 64
    }
    if (B) return 58 + linesOf(t, size || 36, 540, 'k', -0.012) * (size || 36) * 1.06 + (sub ? 14 + tall(sub, 13, G.w, 1.55) : 0) + 52
    return linesOf(t, size || 36, G.w, 'k', size === 56 ? -0.02 : -0.01) * (size || 36) * (size === 40 ? 1.05 : size === 32 ? 1.08 : size === 56 ? 1.15 : 1.1) + (sub ? 12 + tall(sub, 14, G.w, 1.55) : 0) + 48
  }
  const avail = (closer) => (closer ? STOP_CLOSER : STOP_FOOT) - G.top

  /** The soft panel at the foot of the page: a heading and three short items. */
  const closerPanel = (title, rows) => (rows && rows.length ? `<div class="pp-closer">
    <h3 class="pp-closer-h">${esc(title)}</h3>
    <div class="pp-trio">${rows.slice(0, 3).map(([k, v]) => `<div><div class="pp-closer-k">${esc(k)}</div><div class="pp-closer-v">${esc(v)}</div></div>`).join('')}</div>
  </div>` : '')

  // A continued page keeps its chapter number in the label, but Bold's giant
  // number belongs to the first page of the chapter only.
  // Case studies and profiles open on a picture, not a heading, and carry no
  // giant number either.
  const frame = (key, n, num, label, body, { foot = true, closer = '', cls = '', giant = true } = {}) => `<div class="page pp pp-${key.replace(/-\d+$/, '')}${cls ? ` ${cls}` : ''}" data-page="${key}">
  ${header(B && (!giant || /, continued$/.test(label)) ? null : num, label)}
  <div class="pp-body">${body}</div>
  ${closer}
  ${foot ? footer(n) : ''}
</div>`

  /** A panel in the flow: a heading and three items. */
  const trio = (title, rows, { red = true, cls = '' } = {}) => `<div class="pp-panel pp-trio-panel${cls ? ` ${cls}` : ''}">
    <h3 class="pp-panel-h">${esc(title)}</h3>
    <div class="pp-trio">${rows.map(([k, v]) => `<div><div class="pp-trio-k${red ? ' red' : ''}">${k}</div><div class="pp-trio-v">${v}</div></div>`).join('')}</div>
  </div>`
  const trioH = (rows) => 22 + 18 + 22 + Math.max(...rows.map(([k, v]) => tall(stripTags(k), 13, G.cw, 1.3, 'b') + 7 + tall(stripTags(v), 12.5, G.cw, 1.5))) + 22

  const squares = (list, cls = '') => `<ul class="pp-sq${cls ? ` ${cls}` : ''}">${list.join('')}</ul>`

  /* ── The pages, in order ─────────────────────────────────────────────── */
  // Each spec: key, chapter label (null for the cover), contents label, and
  // draw(n, num). `chapter` false means it shares the chapter before it.

  const specs = []
  const add = (key, label, toc, draw, chapter = true) => specs.push({ key, label, toc, draw, chapter })
  /** A section that may run to several pages, each drawn by draw(part, i, last, n, num). */
  const addRun = (key, label, toc, parts, draw, chapter = true) => parts.forEach((part, i) => add(
    parts.length > 1 ? `${key}-${i + 1}` : key,
    i === 0 ? label : `${label}, continued`,
    i === 0 ? toc : null,
    (n, num) => draw(part, i, i === parts.length - 1, n, num, parts.length > 1 ? `${key}-${i + 1}` : key),
    chapter && i === 0,
  ))

  // 1. Cover
  add('cover', null, null, () => cover())

  // 2. At a glance — when there is a price or a service to speak of.
  if (m.investment.total > 0 || m.blocks.length) add('glance', 'At a glance', 'At a glance', (n) => glance(n), false)

  // 3. Letter (paginated once the contents list is known; see below).
  const letterBody = m.overview.body
  const hasLetter = Boolean(text(letterBody) || (editable && m.overview.index >= 0))
  if (hasLetter) add('letter', `A note from ${firstName}`, null, null)

  // 4. The challenge — only when the proposal names one.
  if (m.challenges.length) addRun('challenge', 'The challenge', 'The challenge', challengeParts(), challenge)

  // 5. Approach — the studio's process, every proposal with scope.
  if (m.blocks.length) addRun('approach', 'How we work', 'How we work', approachParts(), approach)

  // 6–7. Scope, as many pages as the services need.
  const scopeParts = paginateScope()
  scopeParts.forEach((part, gi) => add(
    `scope-${gi + 1}`,
    gi === 0 ? 'What you get' : 'What you get, continued',
    gi === 0 ? 'What you get' : null,
    (n, num) => scope(part, gi, gi === scopeParts.length - 1, n, num),
    gi === 0,
  ))

  // Library pages (service, portfolio, other) the studio inserted.
  m.otherPages.forEach((p, i) => addRun(`page-${i + 1}`, p.kind === 'service' ? 'Service' : 'From the studio', null,
    proseParts(p.body, { h: headH(p.title || '') }), (part, pi, last, n, num, key) => libraryPage(p, part, pi, n, num, key), false))

  // 8. Timeline
  const bars = m.blocks.filter(b => b.weeks)
  if (bars.length) addRun('timeline', 'Timeline', 'Timeline', timelineParts(bars), timeline)

  // 9. Options — only when the proposal carries alternatives.
  if (m.options.length >= 2) add('options', 'Options', 'Options', (n, num) => options(n, num))

  // 10. Investment
  if (m.investment.total > 0 || m.investment.monthly > 0 || (editable && m.investment.index >= 0)) {
    addRun('investment', 'Investment', 'Investment', investmentParts(), investment)
  }

  // 11–12. Case studies: library case studies, else the proof section.
  if (m.cases.length) {
    m.cases.forEach((c, i) => add(`case-${i + 1}`, 'Recent work', i === 0 ? 'Recent work' : null,
      (n, num) => (i === 0 ? caseFull(n, num, c) : caseSplit(n, num, c)), i === 0))
  } else if (m.proof.stats.length || m.proof.quotes.length) {
    add('case-1', 'Recent work', 'Recent work', (n, num) => proofPage(n, num))
  }

  // 13–14. Profiles: the owner, when there is a bio; anyone named in content.team.
  if (m.bio || m.owner.avatar || m.ownerEntry) add('profile-owner', 'Who you’ll work with', 'Who you’ll work with', (n, num) => profile(n, num, null))
  m.team.forEach((p, i) => add(`profile-${i + 1}`, 'Who you’ll work with', null, (n, num) => profile(n, num, p, i + 1), false))

  // 15–17. Always.
  addRun('working', 'Working together', 'Working together', workingParts(), working)
  addRun('terms', 'The fine print', 'The fine print', termsParts(), finePrint)
  addRun('next', 'Next steps', 'Next steps', nextParts(), nextSteps)

  // The letter carries the contents list, so its length is known only now.
  if (hasLetter) {
    const at = specs.findIndex(s => s.key === 'letter')
    const parts = letterParts(specs.filter(s => s.toc).length)
    const run = parts.map((part, i) => ({
      key: parts.length > 1 ? `letter-${i + 1}` : 'letter',
      label: i === 0 ? `A note from ${firstName}` : `A note from ${firstName}, continued`,
      toc: null,
      chapter: i === 0,
      draw: (n, num) => letter(part, i, parts.length, n, num),
    }))
    specs.splice(at, 1, ...run)
  }

  // Number pages and chapters.
  let chapter = 0
  specs.forEach((s, i) => {
    s.n = i + 1
    if (s.chapter && s.label) chapter += 1
    s.num = s.label ? (s.chapter ? chapter : (specs.slice(0, i).reverse().find(x => x.chapter && x.label)?.num || null)) : null
    if (s.key === 'glance') s.num = null
  })
  const toc = specs.filter(s => s.toc && s.key !== 'cover').map(s => ({ label: s.toc, n: s.n }))

  return specs.map(s => s.draw(s.n, s.num)).join('\n')

  /* ── Page drawings ───────────────────────────────────────────────────── */

  function cover() {
    const title = m.headline || m.title || 'Untitled proposal'
    const size = title.length > 90 ? ' xs' : title.length > 56 ? ' s' : ''
    const h1 = `<h1 class="pp-cover-h1${size}"${m.headline ? '' : ed('title')}>${esc(title)}</h1>`
    // "Complete Brand proposal for Northside Family Clinic", as the frames set it.
    const what = m.title ? (/proposal/i.test(m.title) ? m.title : `${m.title} proposal`) : 'A proposal'
    const sub = what.includes(client) ? esc(what) : `${esc(what)} for ${esc(client)}`
    const meta = [
      ['Prepared for', m.contact || m.recipient],
      ['Prepared by', m.owner.name],
      ['Date', fmtDate(m.date)],
      ['Valid until', fmtDate(m.closes)],
    ].filter(([, v]) => text(v))
    if (S) {
      const lines = [
        m.contact || m.recipient ? `Prepared for ${esc(m.contact || m.recipient)}` : '',
        `by ${esc(m.owner.name)}`,
        [esc(fmtDate(m.date)), m.closes ? `valid until ${esc(fmtDate(m.closes))}` : ''].filter(Boolean).join(' · '),
      ].filter(Boolean)
      return `<div class="page pp pp-cover cover-swiss" data-page="cover">
  <div class="pp-cover-square">${markSvg({ size: 176, className: 'mark big' })}</div>
  <div class="pp-cover-ref">Proposal${m.reference ? `<br>${esc(m.reference)}` : ''}</div>
  ${h1}
  <div class="pp-cover-rule"></div>
  <div class="pp-cover-sub">${sub}</div>
  <div class="pp-cover-meta-lines">${lines.join('<br>')}</div>
  <div class="pp-cover-lockup">${lockupSvg({ accent: '#FFFFFF', ink: '#FFFFFF', width: 220 })}</div>
</div>`
    }
    if (B) {
      return `<div class="page pp pp-cover cover-bold" data-page="cover">
  <div class="pp-cover-lockup">${lockupSvg({ accent: '#FFFFFF', ink: '#FFFFFF', width: 300 })}</div>
  ${h1}
  <div class="pp-cover-band">
    <div class="pp-cover-sub">${esc(what)}</div>
    <div class="pp-cover-meta-lines">${esc(['For', [m.contact, m.recipient].filter(Boolean).join(', ')].join(' '))}<br>${[`By ${esc(m.owner.name)}`, esc(fmtDate(m.date)), esc(m.reference)].filter(Boolean).join('&nbsp;&nbsp;·&nbsp;&nbsp;')}</div>
  </div>
</div>`
    }
    return `<div class="page pp pp-cover cover-editorial" data-page="cover">
  <div class="pp-cover-lockup">${lockupSvg({ width: 300 })}</div>
  ${m.reference ? `<div class="pp-cover-ref">Proposal&nbsp;&nbsp;${esc(m.reference)}</div>` : ''}
  <div class="pp-cover-rule"></div>
  ${h1}
  <div class="pp-cover-sub">${sub}</div>
  <div class="pp-cover-meta">${meta.map(([k, v]) => `<div><div class="k">${esc(k)}</div><div class="v">${esc(v)}</div></div>`).join('')}</div>
</div>`
  }

  function glance(n) {
    const inv = m.investment
    const split = inv.schedule.length ? `Fixed price, in ${inv.schedule.length === 1 ? 'one payment' : `${inWords(inv.schedule.length).toLowerCase()} payments`}` : 'Fixed price'
    const ovAt = `sections.${m.overview.index}.data.glance`
    const items = m.glance.length
      ? m.glance.slice(0, 5).map((g, i) => val(g, `${ovAt}.${i}`, ''))
      : m.blocks.slice(0, 5).map(b => esc(b.title))
    const list = S || B
      ? squares(items.map(x => `<li>${x}</li>`))
      : `<ul class="pp-dash">${items.map(x => `<li>${x}</li>`).join('')}</ul>`
    const h = `${esc(m.title || 'Your proposal')}${m.recipient && !m.title.includes(m.recipient) ? ` for ${esc(m.recipient)}` : ''}`
    return frame('glance', n, null, 'At a glance', `
    <div class="pp-head pp-head-plain"><h2 class="pp-h1 pp-h1-glance${stripTags(h).length > 46 ? ' long' : ''}">${h}</h2></div>
    <div class="pp-cols2 pp-figs">
      ${inv.total > 0 ? `<div><div class="pp-label">Investment</div><div class="pp-glance-n onum red">${money(inv.total)}</div><div class="pp-muted">${esc(split)}${inv.monthly > 0 ? `, plus ${money(inv.monthly)}/mo` : ''}</div></div>` : ''}
      ${m.totalWeeks ? `<div><div class="pp-label">Timeline</div><div class="pp-glance-n"><span class="onum">${m.totalWeeks}</span><span class="pp-glance-u">week${m.totalWeeks === 1 ? '' : 's'}</span></div><div class="pp-muted">Kickoff within 3 days of yes</div></div>` : ''}
    </div>
    <div class="pp-cols2 pp-lists">
      ${items.length ? `<div><div class="pp-label">What you get</div>${list}</div>` : '<div></div>'}
      <div><div class="pp-label">Why Abstract Creative</div><p class="pp-p">${esc(STANDARD_COPY.whyUs)}</p></div>
    </div>
    <div class="pp-panel pp-nextstep">
      <div><div class="pp-label">Next step</div><div class="pp-next-t">${esc(GLANCE_NEXT)}</div></div>
      ${m.closes ? `<div class="pp-next-v">Valid until<br>${esc(fmtDate(m.closes))}</div>` : ''}
    </div>`, { closer: closerPanel('Your first two weeks', FIRST_TWO_WEEKS) })
  }

  /* Letter ------------------------------------------------------------- */

  function letterParts(tocCount) {
    const html = isHtml(letterBody)
    const body = html ? plain(letterBody) : text(letterBody)
    const width = S ? 440 : B ? 430 : 470
    const size = S || B ? 13.5 : 14
    const lh = S || B ? 1.6 : 1.55
    const items = parasOf(body).map(p => ({ kind: 'p', text: p, h: tall(p, size, width, lh) + 14 }))
    if (!items.length) items.push({ kind: 'p', text: '', h: 60 })
    items.push({ kind: 'sign', h: 26 + 24 + 18 + (m.owner.signature ? 48 : 0) + 40 })
    if (tocCount) items.push({ kind: 'toc', h: 12 + 16 + Math.ceil(tocCount / 2) * 25 + 8 })
    const greet = S ? 120 + 36 : B ? 58 + 38 + 84 : 33 + 42
    return paginate(items, avail(false) - greet, avail(false))
  }

  function letter(part, i, count, n, num) {
    const html = isHtml(letterBody)
    const at = `sections.${m.overview.index}.data.body`
    const ps = part.filter(x => x.kind === 'p')
    const edBody = html ? '' : `${ed(at)}${editable && count > 1 ? ` data-part="${i}"` : ''}`
    const content = ps.length
      ? (ps.some(p => p.text)
        ? `<div class="pp-letter-body"${edBody}>${ps.map(p => para(p.text)).join('')}</div>`
        : `<div class="pp-letter-body"${edBody}><p class="token">Open by naming what they asked for, then what we do about it.</p></div>`)
      : ''
    const half = Math.ceil(toc.length / 2)
    const tocCol = (list) => `<div>${list.map(t => `<div class="pp-toc-row"><span>${esc(t.label)}</span><span class="pp-toc-n">${pad(t.n)}</span></div>`).join('')}</div>`
    const dear = `Dear ${esc(m.contactFirst || client)},`
    const greet = i > 0 ? '' : (S ? heading(num, dear) : `<div class="pp-head"><h2 class="pp-h1 pp-dear">${dear}</h2></div>`)
    const key = count > 1 ? `letter-${i + 1}` : 'letter'
    return frame(key, n, num, i > 0 ? `A note from ${firstName}, continued` : `A note from ${firstName}`, `
    ${greet}
    <div class="pp-letter-grid">
      <div class="pp-letter-main">
        ${content}
        ${part.some(x => x.kind === 'sign') ? `<div class="pp-sign">${m.owner.signature ? `<img class="pp-sig-img" src="${esc(m.owner.signature)}" alt="">` : ''}<div class="pp-sign-n">${esc(m.owner.name)}</div><div class="pp-sign-r">${esc(m.owner.title)}</div></div>` : ''}
      </div>
      ${i === 0 ? `<div class="pp-pull"><p>“${esc(LETTER_PULL)}”</p></div>` : ''}
    </div>
    ${part.some(x => x.kind === 'toc') && toc.length ? `<div class="pp-toc"><div class="pp-label">In this proposal</div><div class="pp-toc-cols">${tocCol(toc.slice(0, half))}${tocCol(toc.slice(half))}</div></div>` : ''}`)
  }

  /* Challenge ---------------------------------------------------------- */

  function challengeParts() {
    const tw = S ? 640 : B ? 624 : 520
    const items = m.challenges.slice(0, 6).map((c, i) => ({
      kind: 'c', c, i,
      h: Math.max(56, 6 + 24 + 8 + tall(c.body, S || B ? 13 : 14, tw, 1.55)) + (S ? 58 : B ? 54 : 41),
      mb: S ? 58 : B ? 54 : 41,
    }))
    if (m.success.length) items.push({ kind: 'success', h: trioH(m.success.slice(0, 3).map(c => [c.title, c.body])) + 20 })
    const hh = headH('What stands in the way', '', B ? 36 : 40)
    return paginate(items, avail(false) - hh, avail(false))
  }

  function challenge(part, i, last, n, num, key) {
    const at = `sections.${m.overview.index}.data`
    return frame(key, n, num, i ? 'The challenge, continued' : 'The challenge', `
    ${i === 0 ? heading(num, 'What stands in the way', '', 'pp-h1-l') : ''}
    ${part.filter(x => x.kind === 'c').map(({ c, i: k }) => `<div class="pp-step pp-chal"><div class="pp-step-n onum${!S || k === 0 ? ' red' : ''}">${k + 1}</div><div><h3 class="pp-step-t">${val(c.title, `${at}.challenges.${c._i}.title`, '[Challenge]')}</h3><div class="pp-step-b">${val(c.body, `${at}.challenges.${c._i}.body`, '[What it costs them]')}</div></div></div>`).join('')}
    ${part.some(x => x.kind === 'success') ? trio('What success looks like', m.success.slice(0, 3).map(c => [val(c.title, `${at}.success.${c._i}.title`, '[Outcome]'), val(c.body, `${at}.success.${c._i}.body`, '')]), { cls: 'pp-success' }) : ''}`)
  }

  /* Approach ----------------------------------------------------------- */

  function approachParts() {
    const tw = S ? 454 : B ? 428 : 400
    const size = S || B ? 13 : 14
    const own = (st) => Math.max(S ? 56 : B ? 50 : 63, 2 + tall(st.what, size, tw, size === 13 ? 1.5 : 1.55) + (st.time ? 6 + tall(`Your time: ${st.time}`, 11, tw, 1.2, 's') : 0))
    const hh = headH(S || B ? 'Five stages.<br>One decision at a time.' : 'Five stages. One decision at a time.', '', B ? 36 : 40)
    // The five stages stay on one page: when longer words would push the last
    // one over, the space between them closes up before anything moves.
    const fits = (gap) => m.stages.reduce((n, st) => n + own(st) + gap, 0) - gap <= avail(true) - hh
    const normal = S ? 50 : B ? 46 : 59
    const gap = fits(normal) ? normal : (fits(28) ? 28 : normal)
    const items = m.stages.map((st, i) => ({ st, i, tight: gap !== normal, h: own(st) + gap, mb: gap }))
    return paginate(items, avail(true) - hh, avail(true))
  }

  function approach(part, i, last, n, num, key) {
    const stage = (st) => (st.range ? weeksLabel(st.range[0], st.range[1]) : '')
    const field = (st, key, v) => (st.path ? val(v, `${st.path}.${key}`, '') : esc(v))
    return frame(key, n, num, i ? 'How we work, continued' : 'How we work', `
    ${i === 0 ? heading(num, S || B ? 'Five stages.<br>One decision at a time.' : 'Five stages. One decision at a time.', '', 'pp-h1-l') : ''}
    <div class="pp-stages${part.some(x => x.tight) ? ' tight' : ''}">
    ${part.map(({ st, i: k }) => `<div class="pp-stage">
      <div class="pp-stage-n${B ? ' onum red' : ''}">${B ? k + 1 : pad(k + 1)}</div>
      <div class="pp-stage-h"><h3 class="pp-stage-t">${field(st, 'name', st.name)}</h3>${stage(st) ? `<div class="pp-label">${stage(st)}</div>` : ''}</div>
      <div class="pp-stage-b"><div class="pp-p">${field(st, 'what', st.what)}</div>${st.time ? `<div class="pp-time">Your time: ${field(st, 'time', st.time)}</div>` : ''}</div>
    </div>`).join('')}
    </div>`, { closer: closerPanel('You always know where things stand', WHERE_THINGS_STAND) })
  }

  /* Scope -------------------------------------------------------------- */

  function blockHeight(b) {
    const sc = S ? { t: 16, o: 14, ow: 510, dw: 211, step: 24 } : B ? { t: 16, o: 14, ow: 494, dw: 203, step: 24 } : { t: 17, o: 15, ow: 478, dw: 197, step: 26 }
    const left = 40 + 8 + tall(b.title, sc.t, 170, 1.2, 'b') + 8 + 12
    const dl = b.deliverables.map(d => text(d))
    const half = Math.ceil(dl.length / 2)
    let rows = 0
    for (let r = 0; r < half; r += 1) {
      const a = linesOf(dl[r], 11.5, sc.dw) || 1
      const c = linesOf(dl[r + half], 11.5, sc.dw) || 1
      rows += sc.step + (Math.max(a, c) - 1) * 16
    }
    const right = (b.outcome ? 6 + tall(b.outcome, sc.o, sc.ow, 1.35, 'b') + (S || B ? 12 : 16) : 0) + rows
    return Math.max(left, right) + (S || B ? 52 : 46)
  }

  function paginateScope() {
    if (!m.blocks.length) return editable && m.scopeIndex >= 0 ? [{ blocks: [], tail: true, notinc: false }] : []
    const hh = headH(`Everything in ${m.title || 'this proposal'}.`, 'Every service builds on the one before it. Here is exactly what you receive.')
    const items = m.blocks.map(b => ({ kind: 'b', b, h: blockHeight(b), mb: S || B ? 52 : 46 }))
    items.push({ kind: 'included', h: 15 + (S || B ? 150 : 170) + 36, mb: 36 })
    if (m.notIncluded) items.push({ kind: 'notinc', h: 12 + 10 + tall(m.notIncluded, 12, G.w, 1.55) + 24 })
    // The first page closes with how the work is delivered; later pages close
    // with the add-ons when there are any, else run down to the footer.
    const pages = []
    let rest = items
    while (rest.length) {
      const firstPage = pages.length === 0
      const cap = avail(firstPage || m.addons.length > 0) - (firstPage ? hh : 0)
      const [page] = paginate(rest, cap)
      pages.push(page)
      rest = rest.slice(page.length)
    }
    // A last page holding only the closing panels takes a service with it.
    const lastPg = pages[pages.length - 1]
    const prev = pages[pages.length - 2]
    if (prev && !lastPg.some(x => x.kind === 'b') && prev.filter(x => x.kind === 'b').length > 1) {
      const moved = prev.splice(prev.map(x => x.kind).lastIndexOf('b'), 1)[0]
      lastPg.unshift(moved)
    }
    return pages.map(p => ({ blocks: p.filter(x => x.kind === 'b').map(x => x.b), tail: p.some(x => x.kind === 'included'), notinc: p.some(x => x.kind === 'notinc') }))
  }

  function scope(part, gi, last, n, num) {
    const at = `sections.${m.scopeIndex}.data.items`
    const blocks = part.blocks.map((b) => {
      const p = `${at}.${b.index}`
      const nr = m.blocks.indexOf(b) + 1
      const dl = b.deliverables.map((d, di) => `<li>${b.ownDeliverables ? val(d, `${p}.deliverables.${di}`, '[Deliverable]') : esc(d)}</li>`)
      const half = Math.ceil(dl.length / 2)
      return `<div class="pp-svc">
      <div class="pp-svc-l">
        <div class="pp-svc-n onum red">${pad(nr)}</div>
        <h3 class="pp-svc-t">${val(b.title, `${p}.title`, '[Service]')}</h3>
        ${b.start ? `<div class="pp-label">${weeksLabel(b.start, b.end)}</div>` : b.schedule ? `<div class="pp-label">${esc(b.schedule)}</div>` : ''}
      </div>
      <div class="pp-svc-r">
        ${b.outcome ? `<div class="pp-svc-o">${b.outcomeIsDescription ? val(b.outcome, `${p}.description`, '') : esc(b.outcome)}</div>` : ''}
        ${dl.length ? `<div class="pp-dl">${squares(dl.slice(0, half))}${squares(dl.slice(half))}</div>` : ''}
      </div>
      ${editable ? `<span data-drop="${p}" title="Remove">×</span>` : ''}
    </div>`
    }).join('')
    const names = m.blocks.length ? `Everything in ${esc(m.title || 'this proposal')}.` : 'What is delivered.'
    const closer = gi === 0
      ? closerPanel('How it is delivered', HOW_DELIVERED)
      : (m.addons.length ? closerPanel('Popular add-ons', m.addons.slice(0, 3).map(a => [a.label, a.amount > 0 ? `${money(a.amount)}${a.unit}` : 'Priced on request.'])) : '')
    return frame(`scope-${gi + 1}`, n, num, gi === 0 ? 'What you get' : 'What you get, continued', `
    ${gi === 0 ? heading(num, names, 'Every service builds on the one before it. Here is exactly what you receive.') : ''}
    ${blocks}
    ${editable && last ? `<span data-add="${at}">+ Deliverable</span>` : ''}
    ${part.tail ? trio('Included on every project', STANDARD_COPY.included.map(([k, v]) => [esc(k), esc(v)]), { red: false, cls: 'pp-included' }) : ''}
    ${part.notinc ? `<div class="pp-notinc"><div class="pp-label">Not included</div><p class="pp-p">${esc(m.notIncluded)}</p></div>` : ''}`, { closer })
  }

  /* Library pages ------------------------------------------------------ */

  /** Paragraphs of prose as items, for a page that may continue. */
  function proseParts(body, { h = 0, width = G.w, size = 13, lh = 1.65 } = {}) {
    const src = isHtml(body) ? plain(body) : text(body)
    const items = parasOf(src).map(p => ({ text: p, h: tall(p, size, width, lh) + 10 }))
    if (!items.length) items.push({ text: '', h: 20 })
    return paginate(items, avail(false) - h, avail(false))
  }

  function libraryPage(p, part, i, n, num, key) {
    const html = isHtml(p.body)
    return frame(key, n, num, p.kind === 'service' ? 'Service' : 'From the studio', `
    ${i === 0 ? `<div class="pp-head pp-head-plain"><h2 class="pp-h1"${ed(`sections.${p.index}.data.title`)}>${esc(p.title || '')}</h2></div>` : ''}
    <div class="pp-prose"${html ? '' : ed(`sections.${p.index}.data.body`)}${editable && !html ? ` data-part="${i}"` : ''}>${part.map(x => (x.text ? para(x.text) : '')).join('')}</div>`)
  }

  /* Timeline ----------------------------------------------------------- */

  function timelineParts(list) {
    const T = m.totalWeeks
    const sub = 'Services overlap where one can start before the last is finished, so nothing waits longer than it has to.'
    const hh = headH(S || B ? `${inWords(T)} weeks<br>from yes to launch.` : `${inWords(T)} weeks from yes to launch.`, sub) - (S ? 0 : 12)
    const ms = m.milestones.length ? (S || B ? 120 : 132) + 30 : 0
    const fixed = 12 + 16 + 15 + 32 + ms // week numbers, the approval line, milestones
    const room = avail(true) - hh - fixed
    const most = S ? 64 : B ? 55 : 69
    const step = Math.max(40, Math.min(most, Math.floor(room / list.length)))
    const per = Math.max(1, Math.floor(room / step))
    const parts = []
    for (let i = 0; i < list.length; i += per) parts.push({ bars: list.slice(i, i + per), step })
    return parts
  }

  function timeline(part, i, last, n, num, key) {
    const T = m.totalWeeks
    const all = m.blocks.filter(b => b.weeks)
    const cols = Array.from({ length: T }, (_, k) => `<span>${k + 1}</span>`).join('')
    const milestones = m.milestones.map(x => [weeksLabel(x.week, x.week), x.label])
    const track = Math.max(18, Math.min(S || B ? 32 : 36, part.step - (S || B ? 23 : 33)))
    const h = S || B ? `${inWords(T)} week${T === 1 ? '' : 's'}<br>from yes to launch.` : `${inWords(T)} week${T === 1 ? '' : 's'} from yes to launch.`
    return frame(key, n, num, i ? 'Timeline, continued' : 'Timeline', `
    ${i === 0 ? heading(num, h, 'Services overlap where one can start before the last is finished, so nothing waits longer than it has to.', 'pp-h1-tl') : ''}
    <div class="pp-gantt" style="--weeks:${T};--step:${part.step}px;--track:${track}px">
      <div class="pp-gantt-row head"><div></div><div class="pp-gantt-cols">${cols}</div></div>
      ${part.bars.map((b) => `<div class="pp-gantt-row"><div class="pp-gantt-name">${esc(b.title)}</div><div class="pp-gantt-track"><i class="${all.indexOf(b) === all.length - 1 ? 'last' : ''}" style="left:${((b.start - 1) / T) * 100}%;width:${(b.weeks / T) * 100}%"></i></div></div>`).join('')}
    </div>
    ${last ? `<div class="pp-approve">You approve each stage before the next one starts.</div>
    ${milestones.length ? `<div class="pp-panel pp-ms"><h3 class="pp-panel-h">Milestones</h3><div class="pp-cols4">${milestones.slice(0, 4).map(([w, t]) => `<div><div class="pp-label">${esc(w)}</div><div class="pp-ms-t">${esc(t)}</div></div>`).join('')}</div></div>` : ''}` : ''}`,
    { closer: closerPanel('What could change the dates', CHANGE_THE_DATES) })
  }

  /* Options ------------------------------------------------------------ */

  function options(n, num) {
    const list = m.options.slice(0, 3)
    const sched = m.investment.schedule
    const sameTerms = [
      ...(sched.length ? [[sched.length === 1 ? 'One payment' : `${inWords(sched.length)} payments`, `${sched.map(p => `${p.pct}% ${text(p.label).toLowerCase()}`).join(', ')}.`]] : []),
      ...SAME_TERMS,
    ]
    const bullets = (items, rec) => (S || B
      ? squares(items.slice(0, 6).map(x => `<li>${esc(x)}</li>`))
      : `<ul class="pp-dash${rec ? '' : ' grey'}">${items.slice(0, 6).map(x => `<li>${esc(x)}</li>`).join('')}</ul>`)
    return frame('options', n, num, 'Options', `
    ${heading(num, 'Choose the scope that fits.', 'You can start with one and add the rest later.')}
    <div class="pp-opts" style="--n:${list.length}">
      ${list.map(o => `<div class="pp-opt${o.recommended ? ' rec' : ''}">
        <div class="pp-opt-rec">${o.recommended ? 'Recommended' : ''}</div>
        <h3 class="pp-opt-t">${esc(o.name)}</h3>
        ${o.investment ? `<div class="pp-opt-p${/\d/.test(o.investment) && o.investment.length < 10 ? ' onum' : ''}${o.recommended ? ' red' : ''}">${esc(o.investment)}</div>` : ''}
        ${o.weeks ? `<div class="pp-label">${esc(o.weeks)}</div>` : ''}
        ${o.structure ? `<p class="pp-p">${esc(o.structure)}</p>` : ''}
        ${o.items.length ? bullets(o.items, o.recommended) : ''}
      </div>`).join('')}
    </div>
    <div class="pp-label">Every option includes</div>
    <div class="pp-cols2 pp-incl">${squares(OPTIONS_INCLUDE.slice(0, 2).map(x => `<li>${esc(x)}</li>`))}${squares(OPTIONS_INCLUDE.slice(2).map(x => `<li>${esc(x)}</li>`))}</div>`,
    { closer: B ? '' : closerPanel('Same terms, whichever you choose', sameTerms) })
  }

  /* Investment --------------------------------------------------------- */

  function invLines() {
    const inv = m.investment
    const at = `sections.${inv.index}.data.rows`
    const t = inv.totals
    const lines = inv.rows.map((r, i) => {
      const amount = Number(r.amount) > 0
        ? `<span${ed(`${at}.${i}.amount`)} data-raw="${Number(r.amount)}">${money(r.amount)}</span>${r.kind === 'retainer' ? '/mo' : ''}`
        : (editable ? `<span${ed(`${at}.${i}.amount`)} data-raw="" class="token">[Amount]</span>` : '')
      return { label: text(r.label), html: `<div class="pp-line"><span>${val(r.label, `${at}.${i}.label`, '[Line]')}</span><span class="pp-line-a">${amount}</span></div>` }
    })
    if (t && t.combinedCredit > 0) lines.push({ label: `Combined engagement credit, ${t.creditPct}%`, html: `<div class="pp-line"><span>Combined engagement credit, ${t.creditPct}%</span><span class="pp-line-a">− ${money(t.combinedCredit)}</span></div>` })
    if (t && t.rushFee > 0) lines.push({ label: `Rush delivery, ${t.rushPct}%`, html: `<div class="pp-line"><span>Rush delivery, ${t.rushPct}%</span><span class="pp-line-a">${money(t.rushFee)}</span></div>` })
    if (inv.monthly > 0) lines.push({ label: 'Ongoing services are billed monthly in advance.', html: '<div class="pp-line pp-muted"><span>Ongoing services are billed monthly in advance.</span><span></span></div>' })
    return lines
  }

  function investmentParts() {
    const inv = m.investment
    const step = S ? 36 : B ? 32 : 40
    const heroH = S ? 390 : B ? 428 : 252
    const splitH = inv.schedule.length ? (S || B ? 40 + 6 + 18 + 4 + 14 + 70 : 42 + 8 + 19 + 5 + 14 + 75) : 0
    const items = [{ kind: 'hero', h: heroH + splitH }]
    const lines = invLines()
    // The line-by-line panel may run over a page: each row is an item, the
    // panel's top goes with the first row and its foot with the last.
    lines.forEach((l, i) => items.push({ kind: 'line', i, h: Math.max(1, linesOf(l.label, S || B ? 14 : 16, G.w - 200)) * step + (i === 0 ? 20 + 13 + 18 : 0) + (i === lines.length - 1 ? 18 + 20 : 0) }))
    if (!lines.length && editable) items.push({ kind: 'line', i: -1, h: 90 })
    if (m.addons.length) items.push({ kind: 'addons', h: 20 + 13 + 18 + m.addons.length * step + 18 + 20, mb: 20 })
    if (inv.note) parasOf(inv.note).forEach(p => items.push({ kind: 'note', text: p, h: tall(p, 12.5, G.w, 1.55) + 10 }))
    else if (editable) items.push({ kind: 'note', text: '', h: 40 })
    const pages = paginate(items, avail(false), avail(false))
    // What the price covers and the payment line are the same on every
    // proposal: they close the last page when there is room, and never make
    // a page of their own.
    const last = pages[pages.length - 1]
    let used = last.reduce((n, x) => n + x.h, 0)
    for (const extra of [{ kind: 'covers', h: (S || B ? 50 : 0) + 16 + 16 + 15 + 7 + 36 }, { kind: 'small', h: 18 + 17 }]) {
      if (used + extra.h <= avail(false)) { last.push(extra); used += extra.h }
    }
    return pages
  }

  function investment(part, i, last, n, num, key) {
    const inv = m.investment
    const at = `sections.${inv.index}.data.rows`
    const price = inv.total > 0 ? money(inv.total) : (inv.monthly > 0 ? `${money(inv.monthly)}/mo` : '')
    const lines = invLines()
    const hasHero = part.some(x => x.kind === 'hero')
    const heroInner = `${S ? '' : `<div class="pp-inv-name">${esc(m.title || 'The investment')}</div>`}
      ${price ? `<div class="pp-inv-price onum${B ? ' white' : ''}">${price}</div>` : ''}
      <div class="pp-inv-cap">Fixed price. Includes everything on the scope pages.</div>`
    const hero = hasHero ? `${S ? heading(num, esc(m.title || 'The investment')) : ''}
    <div class="${B ? 'pp-inv-block' : 'pp-inv-hero'}">${heroInner}</div>
    ${inv.schedule.length ? `<div class="pp-split">${inv.schedule.slice(0, 3).map((p, k) => `<div><div class="pp-split-p onum${k === 0 ? ' red' : ''}">${p.pct}%</div><div class="pp-split-a">${money(p.amount)}</div><div class="pp-split-l">${esc(p.label)}</div></div>`).join('')}</div>` : ''}` : ''
    const mine = part.filter(x => x.kind === 'line')
    const firstLine = mine.length > 0 && mine[0].i <= 0
    const lastLine = mine.length > 0 && (mine[mine.length - 1].i === lines.length - 1 || mine[mine.length - 1].i === -1)
    const panel = mine.length ? `<div class="pp-panel pp-lines">${firstLine ? '<div class="pp-label">The price, line by line</div>' : ''}${mine.map(x => (x.i >= 0 ? lines[x.i].html : '')).join('')}${editable && lastLine ? `<span data-add="${at}">+ Line</span>` : ''}</div>` : ''
    const addons = part.some(x => x.kind === 'addons') ? `<div class="pp-panel pp-lines"><div class="pp-label">Add-ons</div>${m.addons.map(a => `<div class="pp-line"><span>${esc(a.label)}</span><span class="pp-line-a">${a.amount > 0 ? `${money(a.amount)}${a.unit}` : ''}</span></div>`).join('')}</div>` : ''
    const covers = part.some(x => x.kind === 'covers') ? `<div class="pp-covers"><h3 class="pp-panel-h">What the price covers</h3><div class="pp-trio">${PRICE_COVERS.map(([k, v]) => `<div class="pp-cover-i"><div class="pp-cover-k">${esc(k)}</div><div class="pp-cover-v">${esc(v)}</div></div>`).join('')}</div></div>` : ''
    const notes = part.filter(x => x.kind === 'note')
    const note = notes.length ? `<div class="pp-prose pp-inv-note"${ed(`sections.${inv.index}.data.note`)}>${inv.note ? notes.map(x => para(x.text)).join('') : (editable ? '<p class="token">What is included, what is invoiced when, and what is not covered.</p>' : '')}</div>` : ''
    const small = part.some(x => x.kind === 'small') ? '<p class="pp-small">Pay by card or bank transfer in your project portal. Invoices are due within 15 days.</p>' : ''
    return frame(key, n, num, i ? 'Investment, continued' : 'Investment', `${hero}${panel}${addons}${note}${covers}${small}`, { cls: hasHero ? '' : 'pp-inv-cont' })
  }

  /* Case studies, proof ------------------------------------------------ */

  function statsRow(stats, path) {
    return `<div class="pp-stats" style="--n:${Math.min(stats.length, 4)}">${stats.slice(0, 4).map((s, i) => `<div><div class="pp-stat-n onum${i === 0 || B ? ' red' : ''}">${path ? val(s.figure, `${path}.${s._i}.figure`, '') : esc(s.figure)}</div><div class="pp-stat-c">${esc(s.caption || '')}</div></div>`).join('')}</div>`
  }

  /** Paragraphs that fit in `room` px, cut at a paragraph, never mid-line. */
  function fitParas(list, room, width, size = 12.5, lh = 1.6, cols = 1) {
    let used = 0
    return list.filter(p => { used += tall(p, size, width, lh) / cols + 10; return editable || used <= room })
  }

  function caseFull(n, num, c) {
    const html = isHtml(c.body)
    const img = text(c.image_url || c.image)
    const body = parasOf(html ? plain(c.body) : c.body)
    const room = avail(false) - (img ? 330 + 40 : 0) - 60 - tall(c.title || '', 28, G.w, 1.12, 'k') - (m.proof.stats.length ? 150 : 0)
    return frame(`case-${c.index}`, n, num, 'Recent work', `
    ${img ? `<div class="pp-img" style="background-image:url('${esc(img)}')"></div>` : ''}
    ${c.client ? `<div class="pp-label">${esc(c.client)}</div>` : ''}
    <h2 class="pp-h1 pp-h1-case"${ed(`sections.${c.index}.data.title`)}>${esc(c.title || '')}</h2>
    <div class="pp-prose pp-cols-text"${html ? '' : ed(`sections.${c.index}.data.body`)}>${fitParas(body, room, (G.w - 32) / 2, 12.5, 1.6, 2).map(para).join('')}</div>
    ${m.proof.stats.length ? statsRow(m.proof.stats, m.proof.index >= 0 ? `sections.${m.proof.index}.data.stats` : null) : ''}`, { giant: false })
  }

  function caseSplit(n, num, c) {
    const html = isHtml(c.body)
    const img = text(c.image_url || c.image)
    const q = m.proof.quotes[0]
    const body = parasOf(html ? plain(c.body) : c.body)
    const room = avail(false) - 12 - 22 - tall(c.title || '', 28, 300, 1.08, 'k') - 30 - (q ? tall(q.quote, 16, 300, 1.35, 'b') + 50 : 0)
    return frame(`case-${c.index}`, n, num, 'Recent work', `
    <div class="pp-split2">
      <div>
        ${c.client ? `<div class="pp-label">${esc(c.client)}</div>` : ''}
        <h2 class="pp-h1 pp-h1-case"${ed(`sections.${c.index}.data.title`)}>${esc(c.title || '')}</h2>
        <div class="pp-prose"${html ? '' : ed(`sections.${c.index}.data.body`)}>${fitParas(body, room, 300, 13, 1.6).map(para).join('')}</div>
        ${q ? `<div class="pp-quote">“${esc(q.quote)}”</div><div class="pp-muted">${esc([q.name, q.company].filter(Boolean).join(', '))}</div>` : ''}
      </div>
      ${img ? `<div class="pp-img tall" style="background-image:url('${esc(img)}')"></div>` : '<div></div>'}
    </div>`, { giant: false })
  }

  function proofPage(n, num) {
    const p = m.proof
    const at = `sections.${p.index}.data`
    return frame('case-1', n, num, 'Recent work', `
    ${heading(num, 'Results that followed.')}
    ${p.stats.length ? statsRow(p.stats, `${at}.stats`) : ''}
    ${p.quotes.slice(0, 3).map((q) => `<div class="pp-panel pp-q"><div class="pp-quote">“${val(q.quote, `${at}.quotes.${q._i}.quote`, '')}”</div><div class="pp-muted">${esc([q.name, q.company].filter(Boolean).join(', '))}</div></div>`).join('')}`)
  }

  /* Profiles ----------------------------------------------------------- */

  function profile(n, num, person, idx = 0) {
    const own = m.ownerEntry
    const p = person || { name: m.owner.name, role: (own && own.role) || m.owner.title, bio: m.bio || (own && own.bio) || '', avatar: m.owner.avatar, tasks: (own && own.tasks.length ? own.tasks : ON_THIS_PROJECT), line: own ? own.line : '' }
    const tasks = (p.tasks && p.tasks.length ? p.tasks : ON_THIS_PROJECT).slice(0, 6)
    const half = Math.ceil(tasks.length / 2)
    const contactBits = person ? [] : [m.owner.email, m.owner.phone, m.owner.website].filter(Boolean)
    const lead = person ? p.line : 'You work with me from the first conversation to the handover. No account managers in between.'
    // The bio is cut at a paragraph if it would run into the panel below it.
    const tw = p.avatar ? (S ? 412 : B ? 396 : 352) : 560
    const room = 560 - 22 - 40 - (lead ? tall(lead, 15, tw, 1.4, 'b') + 16 : 0)
    const bio = fitParas(parasOf(p.bio), room, tw, 13, 1.6)
    return frame(person ? `profile-${idx}` : 'profile-owner', n, num, 'Who you’ll work with', `
    <div class="pp-prof${p.avatar ? '' : ' noimg'}">
      ${p.avatar ? `<div class="pp-img portrait" style="background-image:url('${esc(p.avatar)}')"></div>` : ''}
      <div>
        ${p.role ? `<div class="pp-label red">${esc(p.role)}</div>` : ''}
        <h2 class="pp-h1 pp-prof-n">${esc(p.name)}</h2>
        ${lead ? `<div class="pp-lead">${esc(lead)}</div>` : ''}
        ${bio.length ? `<div class="pp-prose pp-bio">${bio.map(para).join('')}</div>` : (editable && person ? '<div class="pp-prose pp-bio"><p class="token">A short bio, written in the studio’s Team panel.</p></div>' : '')}
      </div>
    </div>
    <div class="pp-panel pp-tasks"><h3 class="pp-panel-h">On this project</h3><div class="pp-cols2">${squares(tasks.slice(0, half).map(x => `<li>${esc(x)}</li>`))}${squares(tasks.slice(half).map(x => `<li>${esc(x)}</li>`))}</div></div>
    ${contactBits.length ? `<div class="pp-contactline">${contactBits.map(esc).join('&nbsp;&nbsp;&nbsp;·&nbsp;&nbsp;&nbsp;')}</div>` : ''}`, { giant: false })
  }

  /* Working together ------------------------------------------------------ */

  function workingTitle() {
    return B ? 'What we need from you,<br>what you can expect from us.' : 'What we need from you,<br>and what you can expect from us.'
  }

  function workingParts() {
    const rows = Math.max(m.weNeed.length, m.youGet.length)
    const gap = S || B ? 14 : 18
    const itemH = (r) => (r ? 15 + 4 + tall(r[1], 11.5, S || B ? 316 : 300, 1.5) + gap : 0)
    const items = []
    for (let i = 0; i < rows; i += 1) items.push({ kind: 'row', i, h: Math.max(itemH(m.weNeed[i]), itemH(m.youGet[i])) + (i === 0 ? 18 + 19 : 0) + (i === rows - 1 ? 42 : 0), mb: gap })
    items.push({ kind: 'rev', h: 20 + 15 + 7 + tall(STANDARD_COPY.revisionRounds, 11.5, G.w, 1.5) + 20 + 36, mb: 36 })
    const hh = headH(workingTitle(), '', B ? 36 : 32) + (S ? 0 : 14)
    return paginate(items, avail(true) - hh, avail(true))
  }

  function working(part, i, last, n, num, key) {
    const rows = part.filter(x => x.kind === 'row').map(x => x.i)
    const list = (src) => `<ul class="pp-sq big">${rows.map(k => src[k]).filter(Boolean).map(([k, v]) => `<li><div class="pp-li-t">${esc(k)}</div><div class="pp-li-b">${esc(v)}</div></li>`).join('')}</ul>`
    const firstRows = rows.includes(0)
    return frame(key, n, num, i ? 'Working together, continued' : 'Working together', `
    ${i === 0 ? heading(num, workingTitle(), '', 'pp-h1-m') : ''}
    ${rows.length ? `<div class="pp-cols2 pp-work">
      <div>${firstRows ? '<h3 class="pp-panel-h">What we need from you</h3>' : ''}${list(m.weNeed)}</div>
      <div>${firstRows ? '<h3 class="pp-panel-h">What you can expect from us</h3>' : ''}${list(m.youGet)}</div>
    </div>` : ''}
    ${part.some(x => x.kind === 'rev') ? `<div class="pp-panel pp-rev"><h3 class="pp-panel-h">Revision rounds, explained</h3><p class="pp-p">${esc(STANDARD_COPY.revisionRounds)}</p></div>` : ''}`,
    { closer: closerPanel('How we stay in touch', STAY_IN_TOUCH) })
  }

  /* The fine print -------------------------------------------------------- */

  function termsParts() {
    const tw = S ? 304 : B ? 296 : 286
    const clauses = m.terms.map((t, i) => ({ t, i, h: 15 + 4 + tall(t.body, 11, tw, 1.5) + 20 }))
    const hasCloser = m.investment.schedule.length > 0
    const ask = 15 + 34
    const hh = headH('Terms, in plain language.', 'Signing this proposal accepts these terms. They become part of the agreement.', B ? 36 : 32)
    // Two columns: fill the left, then the right, then a new page.
    const pages = []
    let cur = { cols: [[], []] }
    let col = 0
    let used = 0
    const cap = () => avail(hasCloser) - (pages.length === 0 ? hh : 0) - ask
    for (const c of clauses) {
      if (cur.cols[col].length && used + c.h > cap()) {
        if (col === 0) {
          col = 1
        } else {
          pages.push(cur)
          cur = { cols: [[], []] }
          col = 0
        }
        used = 0
      }
      cur.cols[col].push(c)
      used += c.h
    }
    pages.push(cur)
    return pages
  }

  function finePrint(part, i, last, n, num, key) {
    const col = (list) => `<div>${list.map(({ t, i: k }) => `<div class="pp-term"><div class="pp-term-n">${pad(k + 1)}</div><div><div class="pp-term-t">${t.path ? val(t.title, `${t.path}.title`, '[Clause]') : esc(t.title)}</div><div class="pp-term-b">${t.path ? val(t.body, `${t.path}.body`, '[What it says]') : esc(t.body)}</div></div></div>`).join('')}</div>`
    const sched = m.investment.schedule
    const pays = sched.slice(0, 3).map((p, k) => [p.label, k === 0 && m.investment.total ? `${money(p.amount)}, which is ${p.pct}% of ${money(m.investment.total)}.` : `${money(p.amount)}, which is ${p.pct}%.`])
    return frame(key, n, num, i ? 'The fine print, continued' : 'The fine print', `
    ${i === 0 ? heading(num, 'Terms, in plain language.', 'Signing this proposal accepts these terms. They become part of the agreement.', 'pp-h1-m') : ''}
    <div class="pp-cols2 pp-terms">${col(part.cols[0])}${col(part.cols[1])}</div>
    ${last ? `<div class="pp-ask">Questions about any of this? Ask before you sign. ${esc(firstName)} is happy to walk through every line.</div>` : ''}`,
    { closer: sched.length ? closerPanel('Payments at a glance', pays) : '' })
  }

  /* Next steps ------------------------------------------------------------ */

  function nextParts() {
    const acc = m.acceptance
    const items = []
    const hh = headH('Ready when you are.', '', B ? 36 : (S ? 30 : 56)) - (S || B ? 0 : 8)
    if (acc.body) parasOf(acc.body).forEach(p => items.push({ kind: 'acc', text: p, h: tall(p, 13, G.w, 1.65) + 10 }))
    else if (editable && acc.index >= 0) items.push({ kind: 'acc', text: '', h: 40 })
    items.push({ kind: 'steps', h: STANDARD_COPY.nextSteps.length * (S ? 89 : B ? 80 : 97) })
    items.push({ kind: 'sigs', h: 90, mb: 32 })
    if (!B) items.push({ kind: 'contact', h: 128 + 36 + 20, mb: 36 })
    // Bold closes with a red band where the others close with questions.
    const cap = B ? 846 - 24 - G.top : avail(true)
    return paginate(items, cap - hh, cap)
  }

  function nextSteps(part, i, last, n, num, key) {
    const acc = m.acceptance
    const contact = `<div class="pp-contact-l">${lockupSvg(B ? { accent: '#FFFFFF', ink: '#FFFFFF', width: 220 } : { width: 200 })}</div>
      <div class="pp-contact-r"><div class="pp-contact-n">${esc(m.owner.name)}</div><div class="pp-contact-role">${esc(m.owner.title)}</div>
      <div class="pp-contact-lines">${[m.owner.email, m.owner.phone, [m.owner.website, m.owner.address].filter(Boolean).join('&nbsp;&nbsp;·&nbsp;&nbsp;')].filter(Boolean).map(x => `<div>${x.includes('&nbsp;') ? x.split('&nbsp;&nbsp;·&nbsp;&nbsp;').map(esc).join('&nbsp;&nbsp;·&nbsp;&nbsp;') : esc(x)}</div>`).join('')}</div></div>`
    const accP = part.filter(x => x.kind === 'acc')
    const steps = part.some(x => x.kind === 'steps')
    const sigs = part.some(x => x.kind === 'sigs')
    return frame(key, n, num, i ? 'Next steps, continued' : 'Next steps', `
    ${i === 0 ? heading(num, 'Ready when you are.', '', 'pp-h1-xl') : ''}
    ${accP.length ? `<div class="pp-prose pp-acc"${ed(`sections.${acc.index}.data.body`)}>${acc.body ? accP.map(x => para(x.text)).join('') : '<p class="token">What signing here means, and what happens next.</p>'}</div>` : ''}
    ${steps ? `<div class="pp-steps">${STANDARD_COPY.nextSteps.map(([k, v], s) => `<div class="pp-step"><div class="pp-step-n onum${!S || s === 0 ? ' red' : ''}">${s + 1}</div><div><h3 class="pp-step-t">${esc(k)}</h3><div class="pp-step-b">${esc(v)}</div></div></div>`).join('')}</div>` : ''}
    ${sigs ? `<div class="pp-sigs">
      <div class="pp-sig"><div class="pp-sig-space"></div><div class="pp-sig-line"></div><div class="pp-sig-k">Client signature</div><div class="pp-sig-who">${esc([m.contact, m.recipient].filter(Boolean).join(', '))}</div><div class="pp-sig-d">Date</div></div>
      <div class="pp-sig"><div class="pp-sig-space">${m.owner.signature ? `<img src="${esc(m.owner.signature)}" alt="">` : ''}</div><div class="pp-sig-line"></div><div class="pp-sig-k">Abstract Creative</div><div class="pp-sig-who">${esc(m.owner.name)}</div><div class="pp-sig-d">Date</div></div>
    </div>` : ''}
    ${!B && part.some(x => x.kind === 'contact') ? `<div class="pp-panel pp-contact">${contact}</div>` : ''}`,
    {
      foot: !(B && last),
      closer: B ? (last ? `<div class="pp-redband">${contact}</div>` : '') : (last ? closerPanel('Common questions', m.faq) : ''),
    })
  }
}
